// import CollectionModels from './CollectionModels'
import Fuse from 'fuse.js'
// import { buildQuery } from '@/core/helpers'
import { remove as removeDiacritics } from 'diacritics'

// NOTE THIS IS A WORK IN PROGRESS
class Collection {
    /**
     * Create a new collection.
     *
     * @param  {array}  data
     * @param  {object}  options
     *
     * @return {void}
     */
    constructor(data = [], options) {
        this.options = _.defaults(options, {
            fuse: {
                keys: _.map(options ? options.filters : [], 'name'), // still not the best solution...
                threshold: 0.2,
                getFn() {
                    return removeDiacritics(Fuse.config.getFn.apply(this, arguments))
                }
            },
            filters: [],
            primaryKey: 'id'
        })

        this.items = _.cloneDeep(data)
        this.filters = this.options.filters
        this.order = []
        this.loading = false
        this.searchQuery = {}

        if (this.options.fuse.keys.length > 0) {
            this.fuse = new Fuse(this.items, this.options.fuse)
        }
    }

    get sortQuery() {
        const sort = this.order.map((order) => (order.sort === 1 ? '' : '-') + order.name).join(',')

        return { sort }
    }

    /**
     * Create a new Collection from inside.
     *
     * @param  {array}  items
     *
     * @return {Collection}
     */
    new(data) {
        if (!data) {
            data = this.items
        }

        return new Collection(data, this.options)
    }

    /**
     * Create a new Collection as a clone.
     *
     * @return {Collection}
     */
    clone() {
        return this.new()
    }

    /**
     * Get all of the items in the collection.
     *
     * @return {array}
     */
    all() {
        return this.items
    }

    /**
     * Get the collection of items as JSON.
     *
     * @return {string}
     */
    toJson() {
        return JSON.stringify(this.items)
    }

    /**
     * Count the number of items in the collection.
     *
     * @param  {string}  key
     *
     * @return {int}
     */
    count(key) {
        if (!key) {
            return this.items.length
        }

        let count = 0
        this.each((val) => (count += parseFloat(val[key])))
        return count.toFixed(2)
    }

    /**
     * Get all items except for those with the specified keys.
     *
     * @param  {any}  keys
     *
     * @return {Collection}
     */
    except(...keys) {
        this.items = this.map((item) => _.omit(item, keys))

        return this
    }

    /**
     * Run a filter over each of the items.
     *
     * @param  {callable|null}  callback
     *
     * @return {Collection}
     */
    filter(callback) {
        return this.new(_.filter(this.items, (val, key) => callback(val, key)))
    }

    /**
     * Group an associative array by a field.
     *
     * @param  {string}  groupBy
     *
     * @return {Collection}
     */
    groupBy(groupBy) {
        // return new Collection(_.groupBy(this.items, groupBy), this.options);
        return this.new(_.groupBy(this.items, groupBy))
    }

    /**
     * Determine if the collection is empty or not.
     *
     * @return {boolean}
     */
    isEmpty() {
        return _.isEmpty(this.items)
    }

    /**
     * Determine if the collection is empty or not.
     *
     * @return {boolean}
     */
    isNotEmpty() {
        return !_.isEmpty(this.items)
    }

    /**
     * Run over each of the items.
     *
     * @param  {callable}  callback
     *
     * @return {Collection}
     */
    each(callback) {
        _.forEach(this.items, (val, key) => callback(val, key))

        return this
    }

    /**
     * Run a map over each of the items.
     *
     * @param  {callable}  callback
     *
     * @return {Collection}
     */
    map(callback) {
        this.items = _.map(this.items, (val, key) => callback(val, key))

        return this
    }

    unique() {
        return this.new(_.union(this.items))
    }

    uniqueBY(...fields) {
        return this.new(_.unionBy(this.items, fields))
    }

    /**
     * Get the values of a given key.
     *
     * @param  {string}  value
     *
     * @return {Collection}
     */
    pluck(value) {
        return this.new(_.map(this.items, (item) => item[value]))
    }

    /**
     * Push an item onto the end of the collection.
     *
     * @param  {any}  value
     *
     * @return {this}
     */
    push(value) {
        if (value.constructor === Collection) {
            this.items.push(...value.all())
        } else {
            this.items.push(value)
        }

        return this
    }

    /**
     * Take the first {limit} items.
     *
     * @param  {int}  limit
     *
     * @return {Collection}
     */
    take(limit) {
        return this.new(_.take(this.items, limit))
    }

    /**
     * Take the first item.
     *
     * @return {Object}
     */
    first() {
        return _.head(this.items)
    }

    /**
     * ---
     *
     * @return {Object}
     */
    connect(model) {
        this.model = model

        return this
    }

    /**
     * ---
     *
     * @return {Object}
     */
    fetch(params) {
        this.loading = true

        const query = buildQuery({
            // take: this.pagination.meta.per_page,
            // page: this.pagination.meta.current_page,
            ...params,
            ...this.searchQuery,
            ...this.sortQuery
        })

        return this.model.get(query).then((response) => {
            this.items = response.data
            // this.pagination.meta = response.meta || {}
            // this.pagination.links = response.links || {}

            this.mapModel()
            this.loading = false

            return response.data
        })
    }

    /**
     * Skip given {amount} of items.
     *
     * @param  {int}  amount
     *
     * @return {Collection}
     */
    skip(amount) {
        this.items = _.drop(this.items, amount)
        return this
    }

    /**
     * Filter items by the given key value pair.
     *
     * @param  {String}  key
     * @param  {any}  operator
     * @param  {any}  value
     *
     * @return {Collection}
     */
    // whereAsCollection(key, operator, value = null) {
    where(key, operator, value = null) {
        if (_.isArray(key)) {
            return this.new(this.filterArray(key))
        }

        if (arguments.length === 2) {
            value = operator
            operator = '='
        }

        return this.filter(this.operatorForWhere(key, operator, value))
    }

    /**
     * Filter items with array of rules.
     *
     * @param  {Array} rules
     *
     * @return {Array}
     */
    filterArray(rules) {
        const collect = this.new()

        rules.forEach((value) => {
            collect.where(...value)
        })

        return collect.all()
    }

    /**
     * Get an operator checker callback.
     *
     * @param  {string}  key
     * @param  {string}  operator
     * @param  {any}  value
     *
     * @return {bool}
     */
    operatorForWhere(key, operator, value) {
        return (item) => {
            switch (operator) {
                default:
                case '=':
                case '==':
                    return _.get(item, key) == value
                case '!=':
                case '<>':
                    return _.get(item, key) != value
                case '<':
                    return _.get(item, key) < value
                case '>':
                    return _.get(item, key) > value
                case '<=':
                    return _.get(item, key) <= value
                case '>=':
                    return _.get(item, key) >= value
                case '===':
                    return _.get(item, key) === value
                case '!==':
                    return _.get(item, key) !== value
            }
        }
    }

    /**
     * Insert item trough path.
     *
     * @param  {int}  id
     * @param  {object}  data
     * @param  {string|bool}  place
     *
     * @return {number}
     */
    // insert(id, data, place) {
    //     let object = this.find(id);
    //     object = place ? this.findDeep(id, place) : object;
    //     object.push(data);
    // }
    /**
     * Get item by given index.
     *
     * @param  {any}  i
     *
     * @return {number}
     */
    index(i) {
        return this.items[i]
    }

    /**
     * Get index of item in array.
     *
     * @param  {any}  item
     *
     * @return {number}
     */
    indexOf(item) {
        return _.indexOf(this.items, item)
    }

    // /**
    //  * Get the last item from the collection.
    //  *
    //  * @return {Model}
    //  */
    // last() {
    //     return _.last(this.items);
    // }
    // /**
    //  * Clears the Vuejs reactivity.
    //  *
    //  * @param  {object}  item
    //  *
    //  * @return {object}
    //  */
    // clear(item) {
    //     const object = {};
    //     for (var prop in item) {
    //         if (item.hasOwnProperty(prop)) {
    //             object[prop] = item[prop];
    //         }
    //     }
    //     return object;
    // }
    // /**
    //  * DeepFinded for nested collections.
    //  *
    //  * @param  {any}  id
    //  *
    //  * @return {any}
    //  */
    // findDeep(id, place) {
    //     let item = this.find(id);
    //     item = _.get(item, place);
    //     return item;
    // }
    /**
     * Find item by id or Model.
     *
     * @param  {Int|Model}  value
     * @param  {Int|Model}  mode  = true for deep search
     *
     * @return {any}
     */
    find(value) {
        if (_.isArray(value)) {
            return this.filter((item) => value.indexOf(item[this.options.primaryKey]) > -1)
        }

        if (typeof value === 'object') value = value[this.options.primaryKey]

        const item = _.find(this.items, (val) => val[this.options.primaryKey] == value)

        return !item ? null : item
    }

    /**
     * Add item to the collection.
     *
     * @param  {Model}  data
     *
     * @return {void}
     */
    // create(data) {
    //     this.push(data);
    // }
    /**
     * Update collection by given data
     *
     * @param  {object}  data
     * @param  {array}  keys
     *
     * @return {any}
     */
    // update(data, keys = false) {
    //     const item = this.find(data);
    //     if (keys) {
    //         Object.keys(data).forEach((val) => {
    //             if (!_.includes(keys, val)) {
    //                 delete data[val];
    //             }
    //         });
    //     }
    //     for (var prop in data) {
    //         if (data.hasOwnProperty(prop)) {
    //             item[prop] = data[prop];
    //         }
    //     }
    //     return this;
    // }
    /**
     * Remove item from collection.
     *
     * @param  {any}  id
     *
     * @return {this}
     */
    remove(id) {
        const index = this.indexOf(this.find(id))
        this.items.splice(index, 1)
        return this
    }

    /**
     * Remove item from collection by index.
     *
     * @param  {int}  index
     *
     * @return {this}
     */
    // removeByIndex(index, hard) {
    //     // cause bugs if hard is set to true in some cases
    //     if (hard) {
    //         this.originalData.splice(index, 1);
    //     }
    //     this.items.splice(index, 1);
    //     return this;
    // }
    /**
     * Mark item as deleted.
     *
     * @param  {any}  id
     *
     * @return {any}
     */
    // delete(id) {
    //     const item = this.find(id);
    //     if (!item) return false;
    //     item.deleted = 1;
    //     return this.remove(item.id);
    // }
    /**
     * Remove item from collection throgh path.
     *
     * @param  {int}  id
     * @param  {any}  item
     * @param  {string}  place
     *
     * @return {any}
     */
    // removeTrough(id, item, place) {
    //     this.findDeep(id, place).remove(item);
    //     return this;
    // }
    /**
     * Search for value using Fuse.js
     *
     * @param  {string}  value
     *
     * @return {Collection}
     */
    search(value = '') {
        this.searchValue = value

        if (value === '') {
            return this
        }

        const items = this.fuse.search(removeDiacritics(value))

        return this.new(_.map(items, 'item'))
    }

    /**
     * Fetch search request
     *
     * @param  {string}  term
     *
     * @return {Collection}
     */
    searchRequest(query) {
        this.searchQuery = {
            ...this.searchQuery,
            ...query
        }

        if (!query) {
            this.searchQuery = {}
        }

        return this.fetch()
    }

    /**
     * Order items by given name and order.
     *
     * @param  {string}  name
     * @param  {string}  order
     *
     * @return {this}
     */
    // orderByOrder(name, order) {
    //     this.items = _.orderBy(this.items, name, order);
    //     return this;
    // }
    /**
     * Order items by given name.
     *
     * @param  {string}  name
     *
     * @return {void}
     */
    orderBy(name, direction = 'desc') {
        // const item = _.find(this.order, { name })

        // if (item) {
        //   const index = _.indexOf(this.order, item)

        //   if (item.sort === 2) {
        //     this.order.splice(index, 1)
        //   } else {
        //     item.sort++
        //     this.order.splice(index, 1, item)
        //   }
        // } else {
        //   this.order.push({ name, sort: 1 })
        // }

        const items = _.orderBy(this.items, name, direction)

        return this.new(items)
    }

    /**
     * Get properly classes from active order state.
     *
     * @param  {string}  name
     *
     * @return {object}
     */
    // orderClass(name) {
    //   let item = _.find(this.order, {
    //     name: name
    //   })

    //   item = item || {
    //     name: false,
    //     sort: 0
    //   }

    //   return {
    //     active: item.name,
    //     sorting: !item.name,
    //     sorting_desc: item.sort === 1,
    //     sorting_asc: item.sort === 2
    //   }
    // }

    // mapModel(model = null) {
    //   if (model) {
    //     this.model = Vue.prototype[model]
    //   }

    //   this.map(item => new this.model(item))

    //   return this
    // }
}

export default Collection
